import moment from 'moment'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'

import ContentFrame from 'components/Frame/ContentFrame'
import MicroVolumeStimulusTypeIcon from 'components/Program/MicroVolumeConfig/MicroVolumeStimulusTypeIcon'

const TAPER_LEVEL_LABELS = {
    'taper-none': 'Full',
    'taper-one': 'Height 1',
    'taper-two': 'Height 2',
    'taper-three': 'Height 3',
}

const NANO_OVERRIDE_ROWS = [
    { label: 'Bodyweight reps', unilateralKey: 'bodyweightUnilateralRepsOverride', bilateralKey: 'bodyweightBilateralRepsOverride' },
    { label: 'High volume plyo reps', unilateralKey: 'highVolumePlyometricUnilateralRepsOverride', bilateralKey: 'highVolumePlyometricBilateralRepsOverride' },
    { label: 'Med volume plyo reps', unilateralKey: 'mediumVolumePlyometricUnilateralRepsOverride', bilateralKey: 'mediumVolumePlyometricBilateralRepsOverride' },
    { label: 'Low volume plyo reps', unilateralKey: 'lowVolumePlyometricUnilateralRepsOverride', bilateralKey: 'lowVolumePlyometricBilateralRepsOverride' },
    { label: 'Seconds', unilateralKey: 'unilateralSeconds', bilateralKey: 'bilateralSeconds' },
]

function AsCalendarDate(value) {
    if (!value) return null

    if (moment.isMoment(value)) {
        const date = value.clone().startOf('day')
        return date.isValid() ? date : null
    }

    const date = moment(value, 'YYYY-MM-DD', true)
    if (!date.isValid()) return null
    return date.startOf('day')
}

function FormatWeekRange(weekOfTheYearKey) {
    const weekStart = new WeekOfTheYearKey(weekOfTheYearKey).toDate().clone().startOf('day')
    const weekEnd = weekStart.clone().endOf('isoWeek').startOf('day')

    return FormatCalendarDateRange(weekStart, weekEnd)
}

function FormatCalendarDateRange(startValue, endValue) {
    const start = AsCalendarDate(startValue)
    const end = AsCalendarDate(endValue)

    if (!start || !end) return '—'

    if (start.isSame(end, 'year')) {
        return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
    }

    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`
}

function DisplayValue(value) {
    if (value === null || value === undefined || value === '') return '—'
    return value
}

function FormatTaper(microVolume) {
    if (microVolume?.useTaper !== 1) return 'Off'

    const height = TAPER_LEVEL_LABELS[microVolume.taperLevel] ?? microVolume.taperLevel ?? '—'
    const dampener = DisplayValue(microVolume.taperDampener)
    const taperType = DisplayValue(microVolume.taperType)

    return `${height} · ${dampener} · ${taperType}`
}

function FormatBlockLengths(microVolume) {
    const lengths = microVolume?.supportedBlockLengthsJSON

    if (!Array.isArray(lengths) || lengths.length === 0) return '—'

    return lengths.join(', ')
}

function CoversDate(record, date) {
    return Boolean(record?.startDate && record?.endDate && record.startDate <= date && date <= record.endDate)
}

function VolumeForWeek(compiledProgramVolume, weekOfTheYearKey, programMetadata) {
    const phaseTypes = programMetadata?.macroVolumePhaseTypeHashMap
    const focusTypes = programMetadata?.macroVolumeFocusTypeHashMap
    const stimulusTypes = programMetadata?.microVolumeStimulusTypeHashMap
    const programLevels = programMetadata?.programLevelHashMap
    const weekDate = new WeekOfTheYearKey(weekOfTheYearKey).toDate().format('YYYY-MM-DD')

    const macroVolumePhase = (compiledProgramVolume.macroVolumePhases ?? []).find((phase) => CoversDate(phase, weekDate)) ?? null
    const macroVolumeFocus = (compiledProgramVolume.macroVolumeFocuses ?? []).find((focus) => CoversDate(focus, weekDate)) ?? null

    const mesoVolumes = (compiledProgramVolume.mesoVolumes ?? [])
        .filter((mesoVolume) => CoversDate(mesoVolume, weekDate))
        .map((mesoVolume) => ({
            mesoVolume,
            programLevel: programLevels?.[mesoVolume.programLevelId] ?? null,
            macroVolumeFocusType: focusTypes?.[mesoVolume.macroVolumeFocusTypeId] ?? null,
            microVolumes: (mesoVolume.microVolumes ?? [])
                .filter((microVolume) => microVolume.weekOfTheYearKey === weekOfTheYearKey)
                .map((microVolume) => ({
                    microVolume,
                    stimulusType: stimulusTypes?.[microVolume.microVolumeStimulusTypeId] ?? null,
                })),
        }))

    return {
        macroVolumePhase,
        macroVolumePhaseType: phaseTypes?.[macroVolumePhase?.macroVolumePhaseTypeId] ?? null,
        macroVolumeFocus,
        macroVolumeFocusType: focusTypes?.[macroVolumeFocus?.macroVolumeFocusTypeId] ?? null,
        mesoVolumes,
    }
}

function CompiledVolumeRange(compiledProgramVolume) {
    const phases = compiledProgramVolume?.macroVolumePhases ?? []

    if (phases.length === 0) return null

    return FormatCalendarDateRange(phases[0].startDate, phases[phases.length - 1].endDate)
}

export default function VolumeDisplayMode({
    programMetadata = {},
    compiledProgramVolume = null,
    selectedWeekOfTheYearKey,
}) {
    const weekRangeLabel = selectedWeekOfTheYearKey ? FormatWeekRange(selectedWeekOfTheYearKey) : null
    const hasCompiledVolume = Boolean(compiledProgramVolume?.mesoVolumes?.length || compiledProgramVolume?.macroVolumePhases?.length)
    const weekVolume = selectedWeekOfTheYearKey && hasCompiledVolume ? VolumeForWeek(compiledProgramVolume, selectedWeekOfTheYearKey, programMetadata) : null
    const hasActiveVolume = Boolean(weekVolume?.macroVolumePhase || weekVolume?.macroVolumeFocus || weekVolume?.mesoVolumes.length)
    const compiledRange = CompiledVolumeRange(compiledProgramVolume)
    const macroVolumePhase = weekVolume?.macroVolumePhase ?? null
    const macroVolumePhaseType = weekVolume?.macroVolumePhaseType ?? null
    const macroVolumeFocus = weekVolume?.macroVolumeFocus ?? null
    const macroVolumeFocusType = weekVolume?.macroVolumeFocusType ?? null
    const mesoVolumes = weekVolume?.mesoVolumes ?? []
    const focusColor = macroVolumeFocusType?.colorHex
    const nanoGroups = mesoVolumes.flatMap(({ mesoVolume, microVolumes }) => (
        microVolumes.map(({ microVolume, stimulusType }) => ({
            key: microVolume.microVolumeId ?? `${mesoVolume.mesoVolumeId}-${microVolume.weekOfTheYearKey}`,
            label: stimulusType?.name || 'Micro',
            workoutConfigs: microVolume.workoutConfigs ?? [],
        }))
    ))

    return (
        <ContentFrame className="h-[580px] !items-stretch !justify-start">
            <div className="flex h-full min-h-0 w-full flex-col text-left">
                <div className="shrink-0 border-b border-gray-200 px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">
                        {selectedWeekOfTheYearKey || 'No week selected'}
                    </p>
                    {weekRangeLabel && (
                        <p className="text-xs text-gray-500">{weekRangeLabel}</p>
                    )}
                </div>

                {!selectedWeekOfTheYearKey && (
                    <div className="flex min-h-0 flex-1 items-center justify-center">
                        <p className="text-sm text-gray-400">Select a week on the timeline.</p>
                    </div>
                )}

                {selectedWeekOfTheYearKey && !hasCompiledVolume && (
                    <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-center">
                        <p className="text-sm text-gray-400">
                            Generate a preview to view volume for this week.
                        </p>
                    </div>
                )}

                {selectedWeekOfTheYearKey && hasCompiledVolume && !hasActiveVolume && (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
                        <p className="text-sm text-gray-400">
                            No volume is active for {weekRangeLabel}.
                        </p>
                        <p className="text-xs text-gray-400">
                            {programMetadata?.name || 'Program'}{compiledRange ? ` · ${compiledRange}` : ''}
                        </p>
                    </div>
                )}

                {hasActiveVolume && (
                    <div className="grid h-full min-h-0 grid-cols-2 divide-x divide-gray-200">
                        <div className="min-h-0 min-w-0 overflow-y-auto px-4 py-3 [scrollbar-width:thin]">
                            <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-900">
                                    {programMetadata?.name || 'Program'}
                                </p>

                                <div className="space-y-2">
                                    <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Macro</p>

                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { label: 'Phase', type: macroVolumePhaseType, record: macroVolumePhase, recordId: macroVolumePhase?.macroVolumePhaseId },
                                            { label: 'Focus', type: macroVolumeFocusType, record: macroVolumeFocus, recordId: macroVolumeFocus?.macroVolumeFocusId },
                                        ].map((macroCard) => (
                                            <div key={macroCard.label} className="min-w-0 flex-1 space-y-2 rounded-lg border border-gray-200 p-3">
                                                <p className="text-xxs text-gray-400">{macroCard.label}</p>

                                                {macroCard.record ? (
                                                    <>
                                                        <span className="inline-flex items-center gap-x-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xxs font-medium text-gray-700">
                                                            <span
                                                                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                                                style={{ backgroundColor: macroCard.type?.colorHex || '#d1d5db' }}
                                                            />
                                                            {macroCard.type?.name || 'Unknown'}
                                                        </span>
                                                        <p className="text-xs text-gray-600">
                                                            {FormatCalendarDateRange(macroCard.record.startDate, macroCard.record.endDate)}
                                                        </p>
                                                        {macroCard.recordId && (
                                                            <p className="break-all font-mono text-[11px] leading-tight text-gray-400">
                                                                {macroCard.recordId}
                                                            </p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-xs text-gray-400">None active this week</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {mesoVolumes.length === 0 && (
                                    <div className="space-y-2">
                                        <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Meso</p>
                                        <p className="text-xs text-gray-400">No meso volume covers this week</p>
                                    </div>
                                )}

                                {mesoVolumes.map(({ mesoVolume, programLevel, macroVolumeFocusType: mesoFocusType, microVolumes }) => (
                                    <div
                                        key={mesoVolume.mesoVolumeId ?? `${mesoVolume.startDate}-${mesoVolume.programLevelId}`}
                                        className="space-y-3 border-t border-gray-100 pt-3"
                                    >
                                        <div className="space-y-2">
                                            <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Meso</p>

                                            <p className="text-sm font-medium text-gray-900">
                                                {mesoVolume.name || 'Block'}
                                            </p>

                                            <div className="flex flex-wrap items-center gap-2">
                                                {programLevel && (
                                                    <span className="inline-flex items-center gap-x-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xxs font-medium text-gray-700">
                                                        <span
                                                            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                                            style={{ backgroundColor: programLevel.colorHex || '#d1d5db' }}
                                                        />
                                                        {`${programLevel.name}-${DisplayValue(mesoVolume.level)}`}
                                                    </span>
                                                )}

                                                {mesoFocusType && (
                                                    <span className="inline-flex items-center gap-x-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xxs font-medium text-gray-700">
                                                        <span
                                                            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                                            style={{ backgroundColor: mesoFocusType.colorHex || '#d1d5db' }}
                                                        />
                                                        {mesoFocusType.name || 'Unknown'}
                                                    </span>
                                                )}
                                            </div>

                                            <p className="text-xs text-gray-600">
                                                Score {DisplayValue(mesoVolume.programLevelScoreLowerBound)}–{DisplayValue(mesoVolume.programLevelScoreUpperBound)}
                                                {' · '}
                                                {FormatCalendarDateRange(mesoVolume.startDate, mesoVolume.endDate)}
                                            </p>

                                            {mesoVolume.description ? (
                                                <p className="text-xs text-gray-500">{mesoVolume.description}</p>
                                            ) : null}

                                            {mesoVolume.mesoVolumeId && (
                                                <p className="break-all font-mono text-[11px] leading-tight text-gray-400">
                                                    {mesoVolume.mesoVolumeId}
                                                </p>
                                            )}
                                        </div>

                                        {microVolumes.length === 0 && (
                                            <div className="space-y-2">
                                                <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Micro</p>
                                                <p className="text-xs text-gray-400">No micro volume covers this week</p>
                                            </div>
                                        )}

                                        {microVolumes.map(({ microVolume, stimulusType }) => (
                                            <div key={microVolume.microVolumeId ?? microVolume.weekOfTheYearKey} className="space-y-2">
                                                <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Micro</p>

                                                <div className="flex items-center gap-2">
                                                    <div
                                                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md"
                                                        style={{ backgroundColor: mesoFocusType?.colorHex || focusColor || '#e5e7eb' }}
                                                    >
                                                        <MicroVolumeStimulusTypeIcon iconKey={stimulusType?.iconKey} />
                                                    </div>

                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {stimulusType?.name || 'Unknown stimulus'}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            Sort {DisplayValue(microVolume.sortOrder)} · {microVolume.weekOfTheYearKey || '—'} · {FormatCalendarDateRange(microVolume.startDate, microVolume.endDate)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                    <div>
                                                        <dt className="text-xxs text-gray-400">Taper</dt>
                                                        <dd className="text-xs text-gray-800">{FormatTaper(microVolume)}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-xxs text-gray-400">Block lengths</dt>
                                                        <dd className="text-xs text-gray-800">{FormatBlockLengths(microVolume)}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-xxs text-gray-400">Regression</dt>
                                                        <dd className="text-xs text-gray-800">{DisplayValue(microVolume.exerciseRegressionLevel)}</dd>
                                                    </div>
                                                </dl>

                                                {microVolume.microVolumeId && (
                                                    <p className="break-all font-mono text-[11px] leading-tight text-gray-400">
                                                        {microVolume.microVolumeId}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="min-h-0 min-w-0 overflow-y-auto px-4 py-4 [scrollbar-width:thin]">
                            <div className="space-y-3">
                                <p className="text-xxs font-semibold uppercase tracking-wide text-gray-400">Nano</p>

                                {nanoGroups.length === 0 && (
                                    <p className="text-xs text-gray-400">No micro volume covers this week</p>
                                )}

                                {nanoGroups.map((group) => (
                                    <div key={group.key} className="space-y-2">
                                        {nanoGroups.length > 1 && (
                                            <p className="text-xs font-medium text-gray-500">{group.label}</p>
                                        )}

                                        {group.workoutConfigs.length === 0 && (
                                            <p className="text-xs text-gray-400">No workout volume on this micro</p>
                                        )}

                                        {group.workoutConfigs.map((workoutConfig) => (
                                            <div key={workoutConfig.workoutConfigId ?? workoutConfig.name} className="overflow-hidden rounded-lg border border-gray-200">
                                                <div className="flex items-center justify-between gap-3 bg-gray-100 px-3 py-2">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {workoutConfig.name || 'Workout'}
                                                    </p>
                                                    <p className="text-xxs text-gray-500">
                                                        {workoutConfig.tag || 'untagged'} · {DisplayValue(workoutConfig.maximumAssignmentsPerWeek)} / week
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-[1fr_3.5rem_3.5rem] bg-gray-50 px-3 py-1 text-xxs text-gray-500">
                                                    <p>Volume overrides</p>
                                                    <p className="text-center">Unilat</p>
                                                    <p className="text-center">Bilat</p>
                                                </div>

                                                {NANO_OVERRIDE_ROWS.map((row) => (
                                                    <div
                                                        key={row.label}
                                                        className="grid grid-cols-[1fr_3.5rem_3.5rem] items-center border-t border-gray-100 px-3 py-1 text-xs"
                                                    >
                                                        <p className="text-gray-500">{row.label}</p>
                                                        <p className="text-center font-semibold text-gray-900">{DisplayValue(workoutConfig[row.unilateralKey])}</p>
                                                        <p className="text-center font-semibold text-gray-900">{DisplayValue(workoutConfig[row.bilateralKey])}</p>
                                                    </div>
                                                ))}

                                                {(workoutConfig.workoutPhaseConfigs ?? []).map((workoutPhase) => (
                                                    <div key={workoutPhase.workoutPhaseConfigId ?? workoutPhase.name}>
                                                        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-3 py-1.5">
                                                            <p className="text-sm text-gray-900">{workoutPhase.name || 'Phase'}</p>
                                                            <p className="text-xs text-gray-500">
                                                                <span className="font-semibold text-gray-900">{DisplayValue(workoutPhase.sets)}</span>
                                                                {Number(workoutPhase.sets) === 1 ? ' set' : ' sets'}
                                                            </p>
                                                        </div>

                                                        {(workoutPhase.workoutSlotConfigs ?? []).map((workoutSlot, workoutSlotIndex) => (
                                                            <div
                                                                key={workoutSlot.workoutSlotConfigId ?? workoutSlotIndex}
                                                                className="grid grid-cols-[1.5rem_5.5rem_4.5rem_1fr] items-center gap-2 border-t border-gray-100 px-3 py-1.5 text-xs"
                                                            >
                                                                <p className="text-gray-400">{workoutSlotIndex + 1}</p>
                                                                <p>
                                                                    <span className="font-semibold text-gray-900">{DisplayValue(workoutSlot.unilateralReps)}</span>
                                                                    <span className="text-gray-400"> /side</span>
                                                                </p>
                                                                <p>
                                                                    <span className="font-semibold text-gray-900">{DisplayValue(workoutSlot.bilateralReps)}</span>
                                                                    <span className="text-gray-400"> reps</span>
                                                                </p>
                                                                <p className="text-right text-xxs text-gray-400">
                                                                    {Array.isArray(workoutSlot.supportedWorkoutLengthsJSON) ? workoutSlot.supportedWorkoutLengthsJSON.join(' · ') : ''}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ContentFrame>
    )
}
